import type { FieldDiff } from "../../../lib/columnVersionDiff";
import { FieldDiffRow } from "./FieldDiffRow";

interface ColumnVersionCompareProps {
  fields: FieldDiff[];
  hideUnchanged: boolean;
  oldLabel: string;
  currentLabel: string;
}

/**
 * Container for the column version diff view.
 *
 * Renders two labelled panes (old | current) above a list of FieldDiffRow
 * entries. When `hideUnchanged` is true, unchanged rows are filtered out.
 * Shows an empty-state message when there is nothing to display.
 */
export function ColumnVersionCompare({
  fields,
  hideUnchanged,
  oldLabel,
  currentLabel,
}: ColumnVersionCompareProps) {
  const visibleFields = hideUnchanged
    ? fields.filter((f) => f.status === "changed")
    : fields;

  return (
    <div className="flex flex-col gap-2 flex-1 min-h-0">
      {/* Pane headers */}
      <div className="grid grid-cols-2 gap-3">
        <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          {oldLabel}
        </div>
        <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          {currentLabel}
        </div>
      </div>

      <div className="overflow-y-auto flex-1 min-h-0 pr-3">
        {visibleFields.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-stone-400">
            No changes
          </div>
        ) : (
          <div className="space-y-4">
            {visibleFields.map((fieldDiff) => (
              <FieldDiffRow key={fieldDiff.key} fieldDiff={fieldDiff} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
